import { TableColumnsType } from "antd";
import { alphabet } from "../utils/AlphabetLetters";
import { StaffsModel } from "../models/staffsModel";
import { formatCurrency, formatCurrencyWithSign } from "../utils/formatCurrency";
import { maskCPF } from "../utils/cpfUtils";
import { formatDate } from "../utils/formatDate";

export const StaffDownloadOptions = [
    { value: "0", label: "Branco" },
    { value: "1", label: "Primeiro Trimestre" },
    { value: "2", label: "Segundo Trimestre" },
    { value: "3", label: "Terceiro Trimestre" },
    { value: "4", label: "Quarto Trimestre" },
    { value: "5", label: "Consolidado" },
];

export const StaffRadioOptions = [
    { value: "0", label: "Completo" },
    { value: "1", label: "Simplificado" },
];

export interface StaffDataTypeFull {
    key: React.Key;
    period: string;
    name: string;
    registration: string;
    contractType: string;
    corporateName: string;
    cpf: string;
    educationLevel: string;
    trainingCourse: string;
    departament: string;
    position: string;
    hrClassification: string;
    admissionDate: Date;
    terminationDate: Date;
    ledgerAccount: string;
    weeklyHoursNum: number;
    totalHours: number;
    totalCost: number;
    actions?: React.ReactNode
}

export interface StaffDataTypeSimple {
    key: React.Key;
    period: string;
    name: string;
    cpf: string;
    registration: string;
    hrClassification: string;
    admissionDate: Date;
    terminationDate: Date;
    actions?: React.ReactNode
}

export const allQuarters = [
    {
        label: "Primeiro Trimestre",
        value: "1",
    },
    {
        label: "Segundo Trimestre",
        value: "2",
    },
    {
        label: "Terceiro Trimestre",
        value: "3",
    },
    {
        label: "Quarto Trimestre",
        value: "4",
    },
]

export const allContractTypeOptions = [
    {
        label: "CLT",
        value: "0",
    },
    {
        label: "Estágio",
        value: "1",
    },
    {
        label: "Pessoa Jurídica",
        value: "2",
    }
]

export const allEducationLevelOptions = [
    {
        label: "Ensino Médio",
        value: "0",
    },
    {
        label: "Nível Técnico",
        value: "1",
    },
    {
        label: "Graduação",
        value: "2",
    },
    {
        label: "Pós-Graduação",
        value: "3",
    },
    {
        label: "Mestrado",
        value: "4",
    },
    {
        label: "Doutorado",
        value: "5",
    },
    {
        label: "Pós-Doutorado",
        value: "6",
    }
]

const quartersOrder = ['Primeiro Trimestre', 'Segundo Trimestre', 'Terceiro Trimestre', 'Quarto Trimestre'];
const contractTypeEnum = ["CLT", "Estágio", "Pessoa Jurídica"];
const educationLevelEnum = ["Ensino Médio", "Nível Técnico", "Graduação", "Pós-Graduação", "Mestrado", "Doutorado", "Pós-Doutorado"];

export const importStaffColumns: TableColumnsType<StaffsModel> = [
    {
        title: 'Nome Completo',
        dataIndex: 'name',
    },
    {
        title: 'Matrícula',
        dataIndex: 'registration',
    },
    {
        title: 'Grau acadêmico',
        dataIndex: 'educationLevel',
        render: (value: string) => {
            return allEducationLevelOptions.find(x => x.value == value)?.label;
        },
    },
    {
        title: 'Período',
        dataIndex: 'period',
        render: (value: string) => {
            return allQuarters.find(x => x.value == value)?.label;
        },
    },
    {
        title: 'Departamento',
        dataIndex: 'department',
    },
    {
        title: 'Cargo',
        dataIndex: 'position',
    },
]

export const staffColumnsFull: TableColumnsType<StaffsModel> = [
    {
        title: 'Período',
        dataIndex: 'period',
        showSorterTooltip: false,
        fixed: 'left',
        filters: [
            {
                text: 'Primeiro Trimestre',
                value: '1'
            },
            {
                text: 'Segundo Trimestre',
                value: '2'
            },
            {
                text: 'Terceiro Trimestre',
                value: '3'
            },
            {
                text: 'Quarto Trimestre',
                value: '4'
            }
        ],
        onFilter: (value, record) => record.period === value,
        sorter: (a, b) => {
            return a.period - b.period
        },
        render: (value: string) => {
            return allQuarters.find(x => x.value == value)?.label;
        },
    },
    {
        title: 'Nome Completo',
        dataIndex: 'name',
        showSorterTooltip: false,
        fixed: 'left',
        sorter: (a, b) => alphabet.indexOf(a.name[0].toLowerCase()) - alphabet.indexOf(b.name[0].toLowerCase()),
    },
    {
        title: 'Matrícula/ID',
        dataIndex: 'registration',
    },
    {
        title: 'Tipo de Contrato',
        dataIndex: 'contractType',
        showSorterTooltip: false,
        filters: [
            {
                text: 'CLT',
                value: '0',
            },
            {
                text: 'Estágio',
                value: '1',
            },
            {
                text: 'Pessoa Jurídica',
                value: '2',
            }
        ],
        onFilter: (value, record) => record.contractType === value,
        sorter: (a, b) => a.contractType - b.contractType,
        render: (value: string) => {
            return allContractTypeOptions.find(x => x.value == value)?.label;
        },
    },
    {
        title: 'Razão Social',
        dataIndex: 'corporateName',
        showSorterTooltip: false,
        sorter: (a, b) => { return (a.corporateName && b.corporateName) ? alphabet.indexOf(a.corporateName[0].toLowerCase()) - alphabet.indexOf(b.corporateName[0].toLowerCase()) : 0 },
    },
    {
        title: 'CPF',
        dataIndex: 'cpf',
        render: (value: string) => {
            return maskCPF(value);
        },
    },
    {
        title: 'Grau Acadêmico',
        dataIndex: 'educationLevel',
        showSorterTooltip: false,
        filters: [
            {
                text: 'Ensino Médio',
                value: '0',
            },
            {
                text: 'Nível Técnico',
                value: '1',
            },
            {
                text: 'Graduação',
                value: '2',
            },
            {
                text: 'Pós-Graduação',
                value: '3',
            },
            {
                text: 'Mestrado',
                value: '4',
            },
            {
                text: 'Doutorado',
                value: '5',
            },
            {
                text: 'Pós-Doutorado',
                value: '6',
            }
        ],
        onFilter: (value, record) => record.educationLevel === value,
        sorter: (a, b) => a.educationLevel - b.educationLevel,
        render: (value: string) => {
            return allEducationLevelOptions.find(x => x.value == value)?.label;
        },
    },
    {
        title: 'Curso de Formação',
        showSorterTooltip: false,
        dataIndex: 'trainingCourse',
        sorter: (a, b) => alphabet.indexOf(a.trainingCourse[0].toLowerCase()) - alphabet.indexOf(b.trainingCourse[0].toLowerCase()),
    },
    {
        title: 'Departamento',
        showSorterTooltip: false,
        dataIndex: 'department',
        sorter: (a, b) => alphabet.indexOf(a.department[0].toLowerCase()) - alphabet.indexOf(b.department[0].toLowerCase()),
    },
    {
        title: 'Cargo',
        showSorterTooltip: false,
        dataIndex: 'position',
        sorter: (a, b) => alphabet.indexOf(a.position[0].toLowerCase()) - alphabet.indexOf(b.position[0].toLowerCase()),
    },
    {
        title: 'Classificação do RH',
        showSorterTooltip: false,
        dataIndex: 'hrClassification',
        sorter: (a, b) => alphabet.indexOf(a.hrClassification[0].toLowerCase()) - alphabet.indexOf(b.hrClassification[0].toLowerCase()),
    },
    {
        title: 'Data de Admissão',
        showSorterTooltip: false,
        dataIndex: 'admissionDate',
        sorter: (a, b) => a.admissionDate.getTime() - b.admissionDate.getTime(),
        render: (value: string) => {
            return formatDate(new Date(value), false);
        }
    },
    {
        title: 'Data de Demissão',
        showSorterTooltip: false,
        dataIndex: 'terminationDate',
        sorter: (a, b) => { return (a.terminationDate && b.terminationDate) ? a.terminationDate.getTime() - b.terminationDate.getTime() : 0 },
        render: (value?: string) => {
            return value ? formatDate(new Date(value), false) : "";
        }
    },
    {
        title: 'Conta Contábil',
        dataIndex: 'ledgerAccount',
    },
    {
        title: 'Nº de Horas Semanais',
        showSorterTooltip: false,
        dataIndex: 'weeklyHoursNum',
        sorter: (a, b) => { return (a.weeklyHoursNum && b.weeklyHoursNum) ? a.weeklyHoursNum - b.weeklyHoursNum : 0 },
    },
    {
        title: 'Horas Totais',
        showSorterTooltip: false,
        dataIndex: 'totalHours',
        sorter: (a, b) => a.staffRubric.totalHours - b.staffRubric.totalHours,
    },
    {
        title: 'Custo Total no Período',
        dataIndex: 'totalCost',
        showSorterTooltip: false,
        sorter: (a, b) => a.staffRubric.totalCost - b.staffRubric.totalCost,
        render: (value: string) => {
            return formatCurrencyWithSign(value);
        },
    },
    {
        title: 'Ações',
        dataIndex: 'actions',
        fixed: 'right'
    },
];

export const staffColumnsSimple: TableColumnsType<StaffsModel> = [
    {
        title: 'Período',
        dataIndex: 'period',
        showSorterTooltip: false,
        filters: [
            {
                text: 'Primeiro Trimestre',
                value: '1'
            },
            {
                text: 'Segundo Trimestre',
                value: '2'
            },
            {
                text: 'Terceiro Trimestre',
                value: '3'
            },
            {
                text: 'Quarto Trimestre',
                value: '4'
            }
        ],
        onFilter: (value, record) => record.period === value,
        sorter: (a, b) => {
            return a.period - b.period
        },
        render: (value: string) => {
            return allQuarters.find(x => x.value == value)?.label;
        },
    },
    {
        title: 'Nome Completo',
        showSorterTooltip: false,
        dataIndex: 'name',
        sorter: (a, b) => alphabet.indexOf(a.name[0].toLowerCase()) - alphabet.indexOf(b.name[0].toLowerCase()),
    },
    {
        title: 'CPF',
        dataIndex: 'cpf',
        render: (value: string) => {
            return maskCPF(value);
        },
    },
    {
        title: 'Matrícula/ID',
        dataIndex: 'registration',
    },
    {
        title: 'Classificação do RH',
        showSorterTooltip: false,
        dataIndex: 'hrClassification',
        sorter: (a, b) => alphabet.indexOf(a.hrClassification[0].toLowerCase()) - alphabet.indexOf(b.hrClassification[0].toLowerCase()),
    },
    {
        title: 'Data de Admissão',
        showSorterTooltip: false,
        dataIndex: 'admissionDate',
        sorter: (a, b) => a.admissionDate.getTime() - b.admissionDate.getTime(),
        render: (value: string) => {
            return formatDate(new Date(value), false);
        }
    },
    {
        title: 'Data de Demissão',
        dataIndex: 'terminationDate',
        showSorterTooltip: false,
        sorter: (a, b) => { return (a.terminationDate && b.terminationDate) ? a.terminationDate.getTime() - b.terminationDate.getTime() : 0 },
        render: (value?: string) => {
            return value ? formatDate(new Date(value), false) : "";
        }
    },
    {
        title: 'Ações',
        dataIndex: 'actions',
    },
];

export const staffColumnsFullAnnual: TableColumnsType<StaffsModel> = [
    {
        title: 'Período',
        dataIndex: 'period',
        showSorterTooltip: false,
        fixed: 'left',
        render: (value: string) => {
            return "Anual";
        },
    },
    {
        title: 'Nome Completo',
        dataIndex: 'name',
        showSorterTooltip: false,
        fixed: 'left',
        sorter: (a, b) => alphabet.indexOf(a.name[0].toLowerCase()) - alphabet.indexOf(b.name[0].toLowerCase()),
    },
    {
        title: 'Matrícula/ID',
        dataIndex: 'registration',
    },
    {
        title: 'Tipo de Contrato',
        dataIndex: 'contractType',
        showSorterTooltip: false,
        filters: [
            {
                text: 'CLT',
                value: '0',
            },
            {
                text: 'Estágio',
                value: '1',
            },
            {
                text: 'Pessoa Jurídica',
                value: '2',
            }
        ],
        onFilter: (value, record) => record.contractType === value,
        sorter: (a, b) => a.contractType - b.contractType,
        render: (value: string) => {
            return allContractTypeOptions.find(x => x.value == value)?.label;
        },
    },
    {
        title: 'Razão Social',
        dataIndex: 'corporateName',
        showSorterTooltip: false,
        sorter: (a, b) => { return (a.corporateName && b.corporateName) ? alphabet.indexOf(a.corporateName[0].toLowerCase()) - alphabet.indexOf(b.corporateName[0].toLowerCase()) : 0 },
    },
    {
        title: 'CPF',
        dataIndex: 'cpf',
        render: (value: string) => {
            return maskCPF(value);
        },
    },
    {
        title: 'Grau Acadêmico',
        dataIndex: 'educationLevel',
        showSorterTooltip: false,
        filters: [
            {
                text: 'Ensino Médio',
                value: '0',
            },
            {
                text: 'Nível Técnico',
                value: '1',
            },
            {
                text: 'Graduação',
                value: '2',
            },
            {
                text: 'Pós-Graduação',
                value: '3',
            },
            {
                text: 'Mestrado',
                value: '4',
            },
            {
                text: 'Doutorado',
                value: '5',
            },
            {
                text: 'Pós-Doutorado',
                value: '6',
            }
        ],
        onFilter: (value, record) => record.educationLevel === value,
        sorter: (a, b) => a.educationLevel - b.educationLevel,
        render: (value: string) => {
            return allEducationLevelOptions.find(x => x.value == value)?.label;
        },
    },
    {
        title: 'Curso de Formação',
        showSorterTooltip: false,
        dataIndex: 'trainingCourse',
        sorter: (a, b) => alphabet.indexOf(a.trainingCourse[0].toLowerCase()) - alphabet.indexOf(b.trainingCourse[0].toLowerCase()),
    },
    {
        title: 'Departamento',
        showSorterTooltip: false,
        dataIndex: 'department',
        sorter: (a, b) => alphabet.indexOf(a.department[0].toLowerCase()) - alphabet.indexOf(b.department[0].toLowerCase()),
    },
    {
        title: 'Cargo',
        showSorterTooltip: false,
        dataIndex: 'position',
        sorter: (a, b) => alphabet.indexOf(a.position[0].toLowerCase()) - alphabet.indexOf(b.position[0].toLowerCase()),
    },
    {
        title: 'Classificação do RH',
        showSorterTooltip: false,
        dataIndex: 'hrClassification',
        sorter: (a, b) => alphabet.indexOf(a.hrClassification[0].toLowerCase()) - alphabet.indexOf(b.hrClassification[0].toLowerCase()),
    },
    {
        title: 'Data de Admissão',
        showSorterTooltip: false,
        dataIndex: 'admissionDate',
        sorter: (a, b) => a.admissionDate.getTime() - b.admissionDate.getTime(),
        render: (value: string) => {
            return formatDate(new Date(value), false);
        }
    },
    {
        title: 'Data de Demissão',
        showSorterTooltip: false,
        dataIndex: 'terminationDate',
        sorter: (a, b) => { return (a.terminationDate && b.terminationDate) ? a.terminationDate.getTime() - b.terminationDate.getTime() : 0 },
        render: (value?: string) => {
            return value ? formatDate(new Date(value), false) : "";
        }
    },
    {
        title: 'Conta Contábil',
        dataIndex: 'ledgerAccount',
    },
    {
        title: 'Nº de Horas Semanais',
        showSorterTooltip: false,
        dataIndex: 'weeklyHoursNum',
        sorter: (a, b) => { return (a.weeklyHoursNum && b.weeklyHoursNum) ? a.weeklyHoursNum - b.weeklyHoursNum : 0 },
    },
    {
        title: 'Horas Totais',
        showSorterTooltip: false,
        dataIndex: 'totalHours',
        sorter: (a, b) => a.staffRubric.totalHours - b.staffRubric.totalHours,
    },
    {
        title: 'Custo Total no Período',
        dataIndex: 'totalCost',
        showSorterTooltip: false,
        sorter: (a, b) => a.staffRubric.totalCost - b.staffRubric.totalCost,
        render: (value: string) => {
            return formatCurrencyWithSign(value);
        },
    },
    {
        title: 'Ações',
        dataIndex: 'actions',
        fixed: 'right'
    },
];

export const staffColumnsSimpleAnnual: TableColumnsType<StaffsModel> = [
    {
        title: 'Período',
        dataIndex: 'period',
        showSorterTooltip: false,
        render: (value: string) => {
            return "Anual";
        },
    },
    {
        title: 'Nome Completo',
        showSorterTooltip: false,
        dataIndex: 'name',
        sorter: (a, b) => alphabet.indexOf(a.name[0].toLowerCase()) - alphabet.indexOf(b.name[0].toLowerCase()),
    },
    {
        title: 'CPF',
        dataIndex: 'cpf',
        render: (value: string) => {
            return maskCPF(value);
        },
    },
    {
        title: 'Matrícula/ID',
        dataIndex: 'registration',
    },
    {
        title: 'Classificação do RH',
        showSorterTooltip: false,
        dataIndex: 'hrClassification',
        sorter: (a, b) => alphabet.indexOf(a.hrClassification[0].toLowerCase()) - alphabet.indexOf(b.hrClassification[0].toLowerCase()),
    },
    {
        title: 'Data de Admissão',
        showSorterTooltip: false,
        dataIndex: 'admissionDate',
        sorter: (a, b) => a.admissionDate.getTime() - b.admissionDate.getTime(),
        render: (value: string) => {
            return formatDate(new Date(value), false);
        }
    },
    {
        title: 'Data de Demissão',
        dataIndex: 'terminationDate',
        showSorterTooltip: false,
        sorter: (a, b) => { return (a.terminationDate && b.terminationDate) ? a.terminationDate.getTime() - b.terminationDate.getTime() : 0 },
        render: (value?: string) => {
            return value ? formatDate(new Date(value), false) : "";
        }
    },
    {
        title: 'Ações',
        dataIndex: 'actions',
    },
];